import { useEffect, useMemo, useState } from "react";
import arrayUtilities from "../../../utilities/arrayUtilities";
import { Polygon, Marker } from "react-leaflet";
import EditablePolygon from "../editablePolygon";
import postal from "postal";
import OpacitySlider from "../../form/opacitySlider";
import WorkflowSelect from "../../form/workflowSelect";
import WeightSelect from "../../form/weightSelect";
import ColorPicker from "../../form/colorPicker";
import ConfigurationDialog from "./_configurationDialog";
import stringUtilities from "../../../utilities/stringUtilities";
import geospatialUtilities from "../../../utilities/geospatialUtilities";
import { DrawingNoteWithPresets } from "../../form/drawingNoteWithPresets";
import * as turf from '@turf/turf'
import MapPopup from "./_mapPopup";
import LimitSelect from "../../form/limitSelect";
import hash from "object-hash";
import sketchUtilities from "../../../utilities/sketchUtilities";
import FontSizeSelect from "../../form/fontSizeSelect";
import WidthSlider from "../../form/widthSlider";
import RotationSlider from "../../form/rotationSlider";
import { getTextMarkerIcon } from "./getTextMarkerIcon";

export default function PolygonDrawingTool(props) {

    const [isDrawing, setIsDrawing] = useState(props.isDrawing);
    const [isEditing, setIsEditing] = useState(props.isEditing);
    const [isEditingNote, setIsEditingNote] = useState(false);        
    const [isVisible, setIsVisible] = useState(false);

    const [printingSketchLimitIndex, setPrintingSketchLimitIndex] = useState(null);
    const [printingWorkflowId, setPrintingWorkflowId] = useState(null);
    const [renderPositions, setRenderPositions] = useState(null);

    const [coords, setCoords] = useState(null);
    const [noteRotate, setNoteRotate] = useState(null);
    const [noteWidth, setNoteWidth] = useState(null);
    const [noteFontsize, setNoteFontsize] = useState(null);
    const [noteColor, setNoteColor] = useState(null);    
    const [noteMidPoint, setNoteMidPoint] = useState(null);
    const [text, setText] = useState(null);
    const [weight, setWeight] = useState(null);
    const [color, setColor] = useState(null);
    const [opacity, setOpacity] = useState(null);
    const [fillOpacity, setFillOpacity] = useState(null);
    const [workflowId, setWorkflowId] = useState(null);
    const [limitId, setLimitId] = useState(null);

    const reset = function () {
        setCoords(props.sketchItem?.coords ?? []);
        setNoteColor(props.sketchItem?.tool?.properties?.noteColor ?? "#000000")
        setNoteFontsize(props.sketchItem?.tool?.properties?.noteFontsize ?? "font-smaller")
        setNoteWidth(props.sketchItem?.tool?.properties?.noteWidth ?? 0)
        setNoteRotate(props.sketchItem?.tool?.properties?.noteRotate ?? 0)
        setText(props.sketchItem?.tool?.properties?.text ?? "")
        setWeight(props.sketchItem?.tool?.properties?.weight ?? 1);
        setColor(props.sketchItem?.tool?.properties?.color ?? "#000000");
        setOpacity(props.sketchItem?.tool?.properties?.opacity ?? 1);
        setFillOpacity(props.sketchItem?.tool?.properties?.fillOpacity ?? 0.5);
        setWorkflowId(props.sketchItem?.tool?.properties?.workflowId ?? "");
        setLimitId(props.sketchItem?.tool?.properties?.limitId ?? props.activeLimit);
    }

    const onDelete = function () {

        if (props.onDelete) {
            props.onDelete(props.index);
        }
    }

    const onCancel = function () {

        if (props.onCancel) {
            props.onCancel();
        }

        setIsDrawing(false);
        setIsEditing(false);
        reset();
    }

    const onSave = function () {

        if (props.onSave) {

            let sketchItem = {
                coords,
                tool: {
                    id: 14,
                    properties: {
                        weight,
                        color,
                        opacity,
                        fillOpacity,
                        workflowId,
                        limitId,
                        text,
                        noteColor,
                        noteFontsize,
                        noteRotate,
                        noteWidth
                    }
                }
            }
            props.onSave(sketchItem, props.index);
        }

        setIsDrawing(props.isDrawing)
        setIsEditing(props.isEditing);
        reset();
    }

    useEffect(() => {

        if (isEditing) {
            postal.publish({
                channel: "map",
                topic: "isDrawingOrEditing",
                data: { value: true }
            })
        }

    }, [isEditing])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(coords)) {
            const positions = geospatialUtilities.coordsToLatLngArray(coords)
            setRenderPositions(positions);
            if (positions) {
                setNoteMidPoint([positions.reduce((s, p) => s + p[0], 0) / positions.length, positions.reduce((s, p) => s + p[1], 0) / positions.length])
            }
        }
    }, [coords])

    useEffect(() => {

        let pointsLinesIsDefault = arrayUtilities.isNullOrEmpty(props.workflowPoints) && arrayUtilities.isNullOrEmpty(props.workflowLines) && arrayUtilities.isNullOrEmpty(props.workflowPointsLines);
        let workflowIsVisible = stringUtilities.isNullOrEmpty(workflowId) || pointsLinesIsDefault || props.workflowPoints.includes(workflowId) || props.workflowLines.includes(workflowId) || props.workflowPointsLines.includes(workflowId);

        setIsVisible(isDrawing || isEditing || workflowIsVisible);
    }, [isDrawing, isEditing, workflowId, props.workflowLines, props.workflowPoints, props.workflowPointsLines])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(coords)) {
            let renderPositions = geospatialUtilities.coordsToLatLngArray(coords);

            if (printingSketchLimitIndex !== null || !stringUtilities.isNullOrEmpty(printingWorkflowId)) {

                if (!sketchUtilities.isSketchObjectLimitCurrentlyPrinting(limitId, props.sketch?.limits, printingSketchLimitIndex)) {
                    renderPositions = null;
                }

                if (renderPositions !== null && !stringUtilities.isNullOrEmpty(workflowId)) {
                    let workflow = arrayUtilities.isNullOrEmpty(props.workflows) ? null : props.workflows.find(x => x.id === workflowId);

                    if (!stringUtilities.isNullOrEmpty(printingWorkflowId) && printingWorkflowId !== "ALL" && printingWorkflowId !== workflowId && (!workflow || workflow === null || !workflow.isComposite)) {
                        renderPositions = null;
                    }
                }
                if (renderPositions !== null && printingSketchLimitIndex !== null && !isNaN(printingSketchLimitIndex) && props.sketch && props.sketch !== null && !arrayUtilities.isNullOrEmpty(props.sketch.limits) && props.sketch.limits.length > printingSketchLimitIndex) {

                    let limit = props.sketch.limits[printingSketchLimitIndex];

                    if (!arrayUtilities.isNullOrEmpty(limit.limitOfSketchCoords)) {
                        let p1 = turf.polygon([geospatialUtilities.makePolygonSelfClosing(
                            geospatialUtilities.coordsToLatLngArray(coords)
                        )]);

                        let p2 = turf.polygon([geospatialUtilities.boundsToLatLngArray(limit.limitOfSketchCoords)]);

                        var intersection = turf.intersect(p1, p2);

                        if (intersection) {
                            renderPositions = intersection.geometry.coordinates;
                        } else {
                            renderPositions = null;
                        }
                    }
                }
            }

            setRenderPositions(renderPositions);
        }

    }, [printingSketchLimitIndex, printingWorkflowId])

    useEffect(() => {
        reset();

        const subscriptions = [
            postal.subscribe({
                channel: "map",
                topic: "printing",
                callback: function (data) {
                    setPrintingSketchLimitIndex(data.sketchLimitIndex);
                    setPrintingWorkflowId(data.workflowId);
                }
            }),
        ]

        return () => {
            subscriptions.forEach(subscription => {
                subscription.unsubscribe();
            });
        }
    }, [])

    let pathOptions = { color, weight, opacity, fill: true, fillOpacity };

    const icon = useMemo(
        () => getTextMarkerIcon(`polygon-text-marker-${props.index}`, text, noteColor, noteFontsize, noteRotate, noteWidth),
        [text, noteColor, noteFontsize, noteRotate, noteWidth]
    )

    const handleDeleteNote = () => {
        if (props.onSave) {

            let sketchItem = {
                coords,
                tool: {
                    id: 14,
                    properties: {
                        weight,
                        color,
                        opacity,
                        fillOpacity,
                        workflowId,
                        limitId
                    }
                }
            }
            props.onSave(sketchItem, props.index);
        }
    }

    return <>

        {(isDrawing || isEditing) &&
            <EditablePolygon pathOptions={ pathOptions } coords={ coords } key={ hash({ coords }) } onChange={ setCoords } isDrawing={ isDrawing } noteComponent={
                renderPositions && !stringUtilities.isNullOrEmpty(text) &&
                <Marker icon={ icon } position={ noteMidPoint } pane="drawing-tool-labels" />
            } />
        }
        {!isDrawing && !isEditing && isVisible && !arrayUtilities.isNullOrEmpty(renderPositions) &&
            <Polygon positions={renderPositions} pathOptions={pathOptions}>
                <MapPopup title="Polygon" onEdit={() => setIsEditing(true)} deleteTitle="Delete Polygon" deleteContent="Are you sure you want to permanently delete this polygon?" onDelete={onDelete} />
                { !stringUtilities.isNullOrEmpty(text) && <Marker icon={ icon } position={ noteMidPoint } pane="drawing-tool-labels">
                    { !isEditingNote && <MapPopup
                        title="Note"
                        onEdit={ () => setIsEditingNote(true) }
                        deleteTitle="Delete Note"
                        deleteContent="Are you sure you want to permanently delete this note"
                        onDelete={ () => handleDeleteNote() } /> }
                </Marker> }
            </Polygon>
        }
        {
                    isEditingNote && 
                    <>
                        <ConfigurationDialog show={ isEditingNote } title="Edit Note" onCancel={ () => {
                            setIsEditingNote(false)
                            reset()
                        } } onSave={ () => {
                            if (props.onSave) {
                                let sketchItem = {
                                    coords,
                                    tool: {
                                        id: 23,
                                        properties: {
                                            weight,
                                            color,
                                            opacity,
                                            fillOpacity,
                                            workflowId,
                                            limitId,
                                            text,
                                            noteColor,
                                            noteFontsize,
                                            noteRotate,
                                            noteWidth
                                        }
                                    }
                                }
        
                                props.onSave(sketchItem, props.index);
                            }
                            setIsEditingNote(false)
                        } }>
                            <DrawingNoteWithPresets text={text} setText={setText} />      
                            <FontSizeSelect value={ noteFontsize } onChange={ setNoteFontsize } />
                            <ColorPicker value={ noteColor } onChange={ setNoteColor } />
                            <RotationSlider value={ noteRotate } onChange={ setNoteRotate } />
                            <WidthSlider value={ noteWidth } onChange={ setNoteWidth } />
                        </ConfigurationDialog>
                    </>
                }
        
        <ConfigurationDialog show={isDrawing || isEditing} title={`${isDrawing ? "Add" : "Edit"} Polygon`} onCancel={onCancel} onSave={onSave}>
            <DrawingNoteWithPresets text={text} setText={setText} /> 
            <WeightSelect value={weight} onChange={setWeight} />
            <ColorPicker value={color} onChange={setColor} />
            <OpacitySlider value={opacity} onChange={setOpacity} />
            <OpacitySlider label="Fill Opacity" value={fillOpacity} onChange={setFillOpacity} />
            <WorkflowSelect value={workflowId} onChange={setWorkflowId} workflows={props.workflows} />
            <LimitSelect value={limitId} onChange={setLimitId} limits={props.sketch?.limits} />
        </ConfigurationDialog>
    </>
}